import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import { useAuth } from '../../contexts/AuthContext';
import { useFontSize } from '../../contexts/FontSizeContext';
import { getPageTitleSizeClass, getSmallTextSizeClass, getTextSizeClass } from '../../utils/fontSizeUtils';

const FEEDBACK_TYPES = [
  'Bug report',
  'Account-related issue or request',
  'Billing or payment',
  'Product suggestion',
  'General feedback',
  'General inquiry',
];

const ContactUs: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { fontSize } = useFontSize();
  const [email, setEmail] = useState('');
  const [type, setType] = useState('');
  const [feedback, setFeedback] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isTypeDropdownOpen, setIsTypeDropdownOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const honeypotRef = useRef<HTMLInputElement>(null);
  const typeDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.title = 'Stock Table - Contact Us';
    window.scrollTo(0, 0);
    // Pre-fill email if user is authenticated
    if (isAuthenticated && user?.email) {
      setEmail(user.email);
    }
  }, [isAuthenticated, user]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (typeDropdownRef.current && !typeDropdownRef.current.contains(event.target as Node)) {
        setIsTypeDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    
    // Check if adding these files would exceed the limit
    if (attachments.length + files.length > 3) {
      setError('You can attach a maximum of 3 files.');
      return;
    }

    // Check file sizes (5MB = 5 * 1024 * 1024 bytes)
    const maxSize = 5 * 1024 * 1024;
    const oversizedFiles = files.filter(file => file.size > maxSize);
    
    if (oversizedFiles.length > 0) {
      setError(`Some files exceed the 5MB limit. Please select smaller files.`);
      return;
    }

    setAttachments([...attachments, ...files]);
    setError('');
    
    // Reset the input so the same file can be selected again if needed
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const isFormValid = (): boolean => {
    return email.trim() !== '' && 
           type !== '' && 
           feedback.trim() !== '' &&
           validateEmail(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    // Honeypot check - if this field is filled, it's likely a bot
    if (honeypotRef.current && honeypotRef.current.value !== '') {
      // Silently fail - don't show error to bots
      return;
    }

    if (!email.trim()) {
      setError('Email address is required.');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    if (!type) {
      setError('Please select an inquiry type.');
      return;
    }

    if (!feedback.trim()) {
      setError('Please enter your message.');
      return;
    }

    setIsLoading(true);

    try {
      const formData = new FormData();
      formData.append('email', email);
      formData.append('type', type);
      formData.append('feedback', feedback);
      
      // Add attachments
      attachments.forEach((file, index) => {
        formData.append(`attachments`, file);
      });

      const response = await fetch('/api/v1/feedback', {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        setError('');
        // Reset form
        setEmail(isAuthenticated && user?.email ? user.email : '');
        setType('');
        setFeedback('');
        setAttachments([]);
      } else {
        setError(data.message || 'Failed to send message. Please try again.');
      }
    } catch (error) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-background-light dark:bg-background">
      <Header showSignUp={true} showLogIn={true} />
      
      <main className="flex-grow flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-2xl space-y-8">
          <div>
            <h2 className={`mt-6 text-center ${getPageTitleSizeClass(fontSize)} font-bold tracking-tight text-text-light-primary dark:text-text-primary`}>
              Contact Us
            </h2>
            <p className={`mt-2 text-center ${getSmallTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary`}>
              We'd love to hear from you! Please fill out the form below to contact us.
            </p>
            <p className={`mt-2 text-center ${getSmallTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary`}>
              Your message will be sent to{' '}
              <a 
                href="mailto:service@stock-table.com"
                className="font-medium text-gold-text-light dark:text-highlight-gold hover:text-opacity-80 transition-colors underline"
              >
                service@stock-table.com
              </a>
            </p>
          </div>
          
          <div className="bg-surface-light dark:bg-background-card p-8 rounded-lg shadow-lg border border-border-light dark:border-border-color">
            {success ? (
              <div className="space-y-4">
                <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
                  <p className={`${getSmallTextSizeClass(fontSize)} text-green-600 dark:text-green-400`}>
                    Thank you for contacting us! We've received your message and will get back to you soon.
                  </p>
                </div>
                <button
                  onClick={() => navigate('/daily')}
                  className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm ${getTextSizeClass(fontSize)} font-semibold text-gray-900 bg-primary hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-surface-light dark:focus:ring-offset-background-card focus:ring-primary dark:focus:ring-highlight-gold transition-colors`}
                >
                  Return to Stock Table
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                {/* Honeypot field - hidden from users but visible to bots */}
                <input
                  type="text"
                  ref={honeypotRef}
                  style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                />

                <div>
                  <label className={`block ${getSmallTextSizeClass(fontSize)} font-medium text-text-light-secondary dark:text-text-secondary mb-1`} htmlFor="email">
                    Your Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    autoComplete="email"
                    className={`w-full p-3 bg-white dark:bg-background border rounded-md text-text-light-primary dark:text-text-primary placeholder-text-light-secondary dark:placeholder-text-secondary focus:outline-none focus:ring-1 ${
                      error && !email.trim()
                        ? 'border-red-500 focus:border-red-500 focus:ring-red-500' 
                        : 'border-border-light dark:border-border-color focus:border-primary dark:focus:border-highlight-gold focus:ring-primary dark:focus:ring-highlight-gold'
                    }`}
                    id="email"
                    name="email"
                    required
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    disabled={isAuthenticated}
                  />
                  {isAuthenticated && (
                    <p className={`mt-1 ${getSmallTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary`}>
                      Your email is pre-filled from your account.
                    </p>
                  )}
                </div>

                <div>
                  <label className={`block ${getSmallTextSizeClass(fontSize)} font-medium text-text-light-secondary dark:text-text-secondary mb-1`} htmlFor="type">
                    Inquiry Type <span className="text-red-500">*</span>
                  </label>
                  <div className="relative" ref={typeDropdownRef}>
                    <button
                      type="button"
                      onClick={() => setIsTypeDropdownOpen(!isTypeDropdownOpen)}
                      className={`w-full flex items-center justify-between gap-2 text-gray-900 dark:text-text-primary bg-white dark:bg-border-color border rounded-md px-3 py-1.5 ${getTextSizeClass(fontSize)} font-medium hover:bg-gray-50 dark:hover:bg-opacity-80 transition-colors ${
                        error && !type
                          ? 'border-red-500 focus:border-red-500 focus:ring-red-500' 
                          : 'border-border-light dark:border-border-color focus:border-primary dark:focus:border-highlight-gold focus:ring-primary dark:focus:ring-highlight-gold'
                      }`}
                    >
                      <span className={type ? 'text-gray-900 dark:text-text-primary' : 'text-gray-500 dark:text-text-secondary'}>
                        {type || 'Select a type...'}
                      </span>
                      <svg className="w-4 h-4 text-gray-900 dark:text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path d="M19 9l-7 7-7-7" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"></path>
                      </svg>
                    </button>
                    {isTypeDropdownOpen && (
                      <div className="absolute left-0 mt-2 w-full bg-white dark:bg-background border border-border-light dark:border-border-color rounded-md shadow-lg z-[60] max-h-60 overflow-y-auto">
                        {FEEDBACK_TYPES.map((feedbackType) => (
                          <button
                            key={feedbackType}
                            type="button"
                            onClick={() => {
                              setType(feedbackType);
                              setIsTypeDropdownOpen(false);
                              setError('');
                            }}
                            className={`block w-full text-left px-3 py-1.5 ${getTextSizeClass(fontSize)} transition-colors ${
                              type === feedbackType
                                ? 'text-gray-900 dark:text-text-primary bg-highlight-gold/10 dark:bg-highlight-gold/20 font-semibold'
                                : 'text-gray-700 dark:text-text-primary hover:bg-gray-100 dark:hover:bg-border-color'
                            }`}
                          >
                            {feedbackType}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <input
                    type="hidden"
                    name="type"
                    value={type}
                    required
                  />
                </div>

                <div>
                  <label className={`block ${getSmallTextSizeClass(fontSize)} font-medium text-text-light-secondary dark:text-text-secondary mb-1`} htmlFor="feedback">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    className={`w-full p-3 bg-white dark:bg-background border rounded-md text-text-light-primary dark:text-text-primary placeholder-text-light-secondary dark:placeholder-text-secondary focus:outline-none focus:ring-1 min-h-[150px] resize-y ${
                      error && !feedback.trim()
                        ? 'border-red-500 focus:border-red-500 focus:ring-red-500' 
                        : 'border-border-light dark:border-border-color focus:border-primary dark:focus:border-highlight-gold focus:ring-primary dark:focus:ring-highlight-gold'
                    }`}
                    id="feedback"
                    name="feedback"
                    required
                    value={feedback}
                    onChange={(e) => setFeedback(e.target.value)}
                    placeholder="Please write your message here"
                  />
                </div>

                <div>
                  <label className={`block ${getSmallTextSizeClass(fontSize)} font-medium text-text-light-secondary dark:text-text-secondary mb-1`}>
                    Attachments (optional)
                  </label>
                  <p className={`${getSmallTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary mb-2`}>
                    You can attach up to 3 files, each less than 5MB.
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    onChange={handleFileSelect}
                    className="hidden"
                    id="file-input"
                    accept="*/*"
                  />
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={attachments.length >= 3}
                      className={`px-4 py-2 ${getTextSizeClass(fontSize)} font-medium text-text-light-primary dark:text-text-primary bg-surface-light dark:bg-background border border-border-light dark:border-border-color rounded-md hover:bg-opacity-80 focus:outline-none focus:ring-2 focus:ring-primary dark:focus:ring-highlight-gold disabled:opacity-50 disabled:cursor-not-allowed transition-colors`}
                    >
                      {attachments.length >= 3 ? 'Maximum files reached' : 'Attach Files'}
                    </button>
                    {attachments.length > 0 && (
                      <span className={`${getSmallTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary`}>
                        {attachments.length} file{attachments.length !== 1 ? 's' : ''} attached
                      </span>
                    )}
                  </div>
                  {attachments.length > 0 && (
                    <div className="mt-2 space-y-2">
                      {attachments.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-2 bg-surface-light dark:bg-background border border-border-light dark:border-border-color rounded-md"
                        >
                          <div className="flex-1 min-w-0">
                            <p className={`${getSmallTextSizeClass(fontSize)} text-text-light-primary dark:text-text-primary truncate`}>
                              {file.name}
                            </p>
                            <p className={`${getSmallTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary`}>
                              {formatFileSize(file.size)}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeAttachment(index)}
                            className={`ml-2 px-2 py-1 ${getTextSizeClass(fontSize)} text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors`}
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                {error && (
                  <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                    <p className={`${getSmallTextSizeClass(fontSize)} text-red-600 dark:text-red-400`}>{error}</p>
                  </div>
                )}

                <div>
                  <button
                    className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-semibold text-gray-900 bg-primary hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-surface-light dark:focus:ring-offset-background-card focus:ring-primary dark:focus:ring-highlight-gold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    type="submit"
                    disabled={isLoading || !isFormValid()}
                  >
                    {isLoading ? 'Sending...' : 'Send Message'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ContactUs;

